import { useCallback, useState } from 'react';

import { useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import { getOrCreateAnonymousUserId } from '~/utils/nectarTracking';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME, NECTAR_SITE_ID_FIELD } from '~/utils/nectarTrackingFields';

interface StripeSessionResponse {
  session_id: string;
  url: string;
  is_duplicate: boolean;
}

const useCreateStripePriceSession = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { cookies, cookiesSet, visitToken } = useOutletContext<OutletContext>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const mutateAsync = useCallback(
    async (priceId: string, email?: string, premiumOfferId?: string, isCollectingBillingAddress?: boolean) => {
      setIsLoading(true);

      try {
        const params = new URLSearchParams(searchParams);
        params.set('price_id', priceId);

        if (email) {
          params.set('email', email);
        }

        if (premiumOfferId) {
          params.set('offer_id', premiumOfferId);
        }

        if (cookiesSet && cookies.last_resource_guid && !searchParams.get('last_resource_guid')) {
          params.set('last_resource_guid', cookies.last_resource_guid);
        }

        if (isCollectingBillingAddress) {
          params.set('collect_billing_address', 'true');
        }

        params.set('nectar_upgrade_url', window.location.href);
        params.set('nectar_anonymous_user_id', getOrCreateAnonymousUserId());
        if (site.id) {
          params.set(NECTAR_SITE_ID_FIELD, site.id);
        }

        if (visitToken) {
          params.set('nectar_visit_token', visitToken);
        }

        // page_block rides in from the /upgrade URL (buildUpgradeUrl). It's already
        // copied via `new URLSearchParams(searchParams)` above, but forward it
        // explicitly so it survives if param-copying ever becomes an allowlist.
        const nectarPageBlock = searchParams.get(NECTAR_PAGE_BLOCK_GET_PARAM_NAME);
        if (nectarPageBlock) {
          params.set(NECTAR_PAGE_BLOCK_GET_PARAM_NAME, nectarPageBlock);
        }

        const response = await fetch(`/stripe_session?${params}`);
        const body: StripeSessionResponse = await response.json();
        return body;
      } catch {
        // do nothing
      } finally {
        setIsLoading(false);
      }
    },
    [cookies, cookiesSet, searchParams, site.id, visitToken],
  );

  return {
    mutateAsync,
    isLoading,
  };
};

export default useCreateStripePriceSession;
